import { selectedLandXMLFeatureAtom, selectedLayersAtom } from '@/atoms';
import type { SelectedLandXMLFeature } from '@/atoms/landxmlSelection.atom';
import { projectPointOntoAlignment } from '@/core/landxml';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { ID } from './ID';
import { Button } from './ui/button';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { Label } from './ui/label';

/** Station + offset for the picked point on its own alignment (ALIGNMENT category only). */
function stationAtPoint(feature: SelectedLandXMLFeature): { station: number; offset: number } | null {
  const { polyline, cumLengths } = feature.metadata;
  const click = feature.clickPoint;
  if (!polyline || !cumLengths || !click) return null;
  return projectPointOntoAlignment(new THREE.Vector3(click.x, click.y, click.z), polyline, cumLengths);
}

/** Format a station as `STA d+ddd.dd` (Civil convention). */
function formatStation(station: number): string {
  const sign = station < 0 ? '-' : '';
  const abs = Math.abs(station);
  const major = Math.floor(abs / 1000);
  const minor = abs - major * 1000;
  return `${sign}STA ${major}+${minor.toFixed(2).padStart(6, '0')}`;
}

const LandXMLProperties: FunctionComponent = () => {
  const { projectId } = useParams();
  const [selected, setSelected] = useAtom(selectedLandXMLFeatureAtom);
  const layers = useAtomValue(selectedLayersAtom(projectId!));
  const layer = layers.find((l) => l.id === selected?.layerId);
  const { t } = useTranslation('components');

  const station = useMemo(
    () => (selected && selected.category === 'ALIGNMENT' ? stationAtPoint(selected) : null),
    [selected],
  );

  const handleClose = () => setSelected(null);

  if (!selected) {
    return (
      <Drawer open={false} onClose={handleClose}>
        <DrawerContent />
      </Drawer>
    );
  }
  const m = selected.metadata;

  return (
    <Drawer open={true} onClose={handleClose}>
      <DrawerContent>
        <DrawerHeader className='w-lg'>
          <DrawerTitle>{t('landxmlProperties.title')}</DrawerTitle>
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto'>
          <div className='flex flex-col gap-4 px-4 py-2'>
            <div className='flex flex-col gap-1'>
              <Label>{t('landxmlProperties.layer')}</Label>
              <ID value={layer?.name ?? ''} />
            </div>
            <div className='flex flex-col gap-1'>
              <Label>{t('landxmlProperties.feature')}</Label>
              <span className='text-sm font-mono break-all'>{selected.name}</span>
            </div>
            <div className='flex flex-col gap-1'>
              <Label>{t('landxmlProperties.category')}</Label>
              <span className='text-sm font-mono'>{selected.category}</span>
            </div>

            {selected.category === 'ALIGNMENT' && (
              <div className='flex flex-col gap-1'>
                <Label>{t('landxmlProperties.station')}</Label>
                {station ? (
                  <>
                    <span className='text-sm font-mono'>{formatStation(station.station)}</span>
                    <span className='text-xs text-muted-foreground'>
                      {t('landxmlProperties.offsetFromCenterline', { offset: station.offset.toFixed(3) })}
                    </span>
                  </>
                ) : (
                  <span className='text-xs text-muted-foreground'>—</span>
                )}
              </div>
            )}

            {selected.category !== 'ALIGNMENT' && selected.nearestAlignment && (
              <div className='flex flex-col gap-1'>
                <Label>{t('landxmlProperties.nearestAlignment')}</Label>
                <span className='text-sm font-mono'>{selected.nearestAlignment.name}</span>
                <span className='text-sm font-mono'>{formatStation(selected.nearestAlignment.station)}</span>
                <span className='text-xs text-muted-foreground'>
                  {t('landxmlProperties.offsetFromCenterline', {
                    offset: selected.nearestAlignment.offset.toFixed(3),
                  })}
                </span>
              </div>
            )}

            {/* Metric grid */}
            <div className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
              {m.length !== undefined && (
                <Metric label={t('landxmlProperties.length')} value={`${m.length.toFixed(2)} m`} />
              )}
              {m.surfaceArea !== undefined && (
                <Metric label={t('landxmlProperties.surfaceArea')} value={`${m.surfaceArea.toFixed(2)} m²`} />
              )}
              {m.parcelArea !== undefined && (
                <Metric label={t('landxmlProperties.parcelArea')} value={`${m.parcelArea.toFixed(2)} m²`} />
              )}
              {m.triangleCount !== undefined && (
                <Metric label={t('landxmlProperties.triangleCount')} value={String(m.triangleCount)} />
              )}
              {m.vertexCount !== undefined && (
                <Metric label={t('landxmlProperties.vertexCount')} value={String(m.vertexCount)} />
              )}
              {m.zMin !== undefined && (
                <Metric label={t('landxmlProperties.zMin')} value={`${m.zMin.toFixed(2)} m`} />
              )}
              {m.zMax !== undefined && (
                <Metric label={t('landxmlProperties.zMax')} value={`${m.zMax.toFixed(2)} m`} />
              )}
              {m.diameter !== undefined && (
                <Metric label={t('landxmlProperties.diameter')} value={`${(m.diameter * 1000).toFixed(0)} mm`} />
              )}
              {m.pipeShape !== undefined && (
                <Metric label={t('landxmlProperties.pipeShape')} value={m.pipeShape} />
              )}
              {m.invertStart !== undefined && (
                <Metric label={t('landxmlProperties.invertStart')} value={`${m.invertStart.toFixed(3)} m`} />
              )}
              {m.invertEnd !== undefined && (
                <Metric label={t('landxmlProperties.invertEnd')} value={`${m.invertEnd.toFixed(3)} m`} />
              )}
              {m.slope !== undefined && (
                <Metric label={t('landxmlProperties.slope')} value={`${(m.slope * 100).toFixed(2)} %`} />
              )}
              {m.rimElev !== undefined && (
                <Metric label={t('landxmlProperties.rimElev')} value={`${m.rimElev.toFixed(3)} m`} />
              )}
              {m.sumpElev !== undefined && (
                <Metric label={t('landxmlProperties.sumpElev')} value={`${m.sumpElev.toFixed(3)} m`} />
              )}
              {m.innerDiameter !== undefined && (
                <Metric
                  label={t('landxmlProperties.innerDiameter')}
                  value={`${(m.innerDiameter * 1000).toFixed(0)} mm`}
                />
              )}
            </div>
          </div>
        </div>
        <DrawerFooter>
          <Button variant='link' onClick={handleClose}>
            {t('landxmlProperties.close')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className='flex flex-col gap-0.5'>
    <span className='text-xs text-muted-foreground'>{label}</span>
    <span className='font-mono text-sm break-all'>{value}</span>
  </div>
);

export default LandXMLProperties;
